import { ActionIcon, Button, Group, LoadingOverlay, Modal, Tooltip, useMantineTheme } from '@mantine/core';

import { useDisclosure } from '@mantine/hooks';

import { useCallback, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useParams } from 'react-router-dom';

import { IconLayoutSidebarRightExpand, IconLayoutSidebarRightCollapse } from '@tabler/icons-react';

import { type EditorViewType, SectionEditorNavigation, SectionEditorTitle } from '../../components';
import { AddSection, DeleteSection, RenameSection } from '../../components/Modals';
import { defaultModalProps } from '../../utils/mantineUtils';
import CreateNarrativeDropdown from '../../components/DropdownMenus/CreateNarrativeDropdown';
import { UserAssignmentDropdown } from '../../components/DropdownMenus';

import {
  selectNarrativeIsDirty, selectOutlineIsDirty, selectUpdatedNarrativeText, selectUpdatedOutlineText
} from '../../redux/store';
import { formatSectionsMetadata } from '../../utils/apiUtils';

import useEnsisMutation from '../../hooks/useEnsisMutation';
import { type Proposal, type ProposalSection, type WithoutPermissions } from '../../types/apiTypes';
import { EMPTY_EDITOR_CONTENT } from '../../utils/constants';
import { resetText as resetOutlineText } from '../../redux/OutlineEditorSlice';
import { resetText as resetNarrativeText } from '../../redux/NarrativeEditorSlice';
import { getViewTypeHasUnsavedChanges } from './EditorPane/EditorPane';

interface Props {
  onShowSinglePane: () => void
  onShowSecondaryPane: () => void
  onOpenAddRequirement: () => void
  onOpenClosePaneWarning: () => void
  proposalData: Proposal
  currentSection: WithoutPermissions<ProposalSection>
  canEdit: boolean
  onClickGenerateNarrative: () => void
  rightPaneView: EditorViewType | null
  narrativeExists: boolean
  hasNarrativeOverlayState: boolean
}
const SectionEditorHeaderGroup: React.FC<Props> = (props: Props) => {
  const {
    onShowSinglePane,
    onShowSecondaryPane,
    onClickGenerateNarrative,
    onOpenAddRequirement,
    onOpenClosePaneWarning,
    proposalData,
    currentSection,
    canEdit,
    rightPaneView,
    narrativeExists,
    hasNarrativeOverlayState
  } = props;

  const updatedOutlineText = useSelector(selectUpdatedOutlineText);
  const { updatedContent } = useSelector(selectUpdatedNarrativeText);
  const outlineIsDirty = useSelector(selectOutlineIsDirty);
  const narrativeIsDirty = useSelector(selectNarrativeIsDirty);
  const { other: colorVars } = useMantineTheme();
  const { proposalUid, sectionUid } = useParams();
  const [renameSectionOpened, renameSectionHandlers] = useDisclosure();
  const [deleteSectionOpened, deleteSectionHandlers] = useDisclosure();
  const [addSubsectionOpened, addSubsectionHandlers] = useDisclosure();

  const dispatch = useDispatch();

  const updateSectionMutation = useEnsisMutation(
    `/app/proposal-sections/${sectionUid}/data`,
    {
      requestType: 'patch',
      successMessage: 'Section saved',
      onSuccess: () => { dispatch(resetNarrativeText()); },
      queryKeysToInvalidate: [`/app/proposals/${proposalUid}/data`]
    }
  );

  const saveSectionOutlineMutation = useEnsisMutation(
    '/app/requirement-responses',
    {
      contentType: 'application/json',
      requestType: 'patch',
      successMessage: 'Section saved',
      onSuccess: () => { dispatch(resetOutlineText()); },
      queryKeysToInvalidate: [`/app/proposals/${proposalUid}/sections/${sectionUid}/requirement-responses`]
    }
  );

  const formattedSections = useMemo(() => (
    formatSectionsMetadata(proposalData?.sections ?? [])), [proposalData]);

  const handleSaveNarrative = useCallback(() => {
    updateSectionMutation.mutate({ content: updatedContent === EMPTY_EDITOR_CONTENT ? '' : updatedContent });
  }, [updatedContent]);

  const handleSaveOutline = useCallback(() => {
    const data = {
      proposal_uid: proposalUid ?? '',
      edits: Object.entries(updatedOutlineText)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => ({
          requirement_response_uid: key,
          new_content: value === EMPTY_EDITOR_CONTENT ? '' : value
        }))
    };
    saveSectionOutlineMutation.mutate(data);
  }, [saveSectionOutlineMutation.mutate, updatedOutlineText]);

  const handleSavePage = useCallback(() => {
    if (outlineIsDirty) {
      handleSaveOutline();
    }
    if (narrativeIsDirty) {
      handleSaveNarrative();
    }
  }, [handleSaveNarrative, handleSaveOutline]);

  const isOnlyTopLevelSection = formattedSections.filter(
    (section) => section.parentSectionUid === undefined
  ).length < 2;
  const saveDisabled = !canEdit ||
    !(outlineIsDirty || narrativeIsDirty);

  const handleToggleSecondaryPane = useCallback(() => {
    if (rightPaneView !== null) {
      if (getViewTypeHasUnsavedChanges(
        rightPaneView,
        outlineIsDirty,
        narrativeIsDirty
      )) {
        onOpenClosePaneWarning();
      } else {
        onShowSinglePane();
      }
    } else {
      onShowSecondaryPane();
    }
  }, [
    onShowSecondaryPane,
    onShowSinglePane,
    rightPaneView,
    outlineIsDirty,
    onOpenClosePaneWarning,
    narrativeIsDirty
  ]);

  return <>
    <Modal opened={renameSectionOpened} {...defaultModalProps}>
      <RenameSection
        sections={formattedSections ?? []}
        close={renameSectionHandlers.close}
        sectionToEdit={formattedSections.find((section) => section.uid === sectionUid)}
      />
    </Modal>
    <Modal opened={addSubsectionOpened} {...defaultModalProps}>
      <AddSection
        sections={formattedSections ?? []}
        close={addSubsectionHandlers.close}
        parentSectionUid={sectionUid}
      />
    </Modal>
    <Modal opened={deleteSectionOpened} {...defaultModalProps}>
      <DeleteSection
        sections={formattedSections ?? []}
        close={deleteSectionHandlers.close}
        sectionToEditUid={sectionUid ?? ''}
      />
    </Modal>
    <LoadingOverlay visible={saveSectionOutlineMutation.isPending} />
    <Group justify='space-between' mt={16} mx={16} >
      <SectionEditorTitle
        sectionTitle={currentSection?.title ?? ''}
        sectionUid={sectionUid ?? ''}
        canEdit={canEdit}
        onOpenAddRequirement={onOpenAddRequirement}
        onOpenDelete={deleteSectionHandlers.open}
        onOpenRename={renameSectionHandlers.open}
        onOpenAddSubsection={addSubsectionHandlers.open}
        isOnlyTopLevelSection={isOnlyTopLevelSection}
        parentSection={proposalData.sections?.find(
          (section) => section.uid === currentSection.parent_proposal_section_uid
        )
        }
      />
      <Group gap={16}>
        {narrativeExists
          ? (
              !hasNarrativeOverlayState && (
              <Button disabled={!canEdit} radius={4} variant='outline' onClick={onClickGenerateNarrative}>
                Generate Narrative
              </Button>
              )
            )
          : (
            <CreateNarrativeDropdown
              canEdit={canEdit}
              sectionUid={sectionUid ?? ''}
              onClickGenerateNarrative={onClickGenerateNarrative}
            />
            )
        }
        <Button
          disabled={saveDisabled}
          onClick={handleSavePage}
          radius={4}
        >
          Save
        </Button>
        <SectionEditorNavigation sections={proposalData.sections ?? []} />
        <Tooltip
          label={rightPaneView !== null ? 'Close split view' : 'Open split view'}
        >
          <ActionIcon
            variant='subtle'
            c={colorVars.primaryIconColor}
            onClick={handleToggleSecondaryPane}
          >
            {rightPaneView !== null ? <IconLayoutSidebarRightCollapse /> : <IconLayoutSidebarRightExpand />}
          </ActionIcon>

        </Tooltip>
      </Group>
    </Group >
    <Group mx={16} mb={8}>
      <UserAssignmentDropdown
        sectionUid={currentSection.uid}
      />
    </Group>
  </>;
};

export default SectionEditorHeaderGroup;
